import * as React from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'containers/Tiptap/components/tiptap-ui-primitive/tooltip';

import 'containers/Tiptap/components/tiptap-ui-primitive/button/button-colors.scss';
import 'containers/Tiptap/components/tiptap-ui-primitive/button/button-group.scss';
import 'containers/Tiptap/components/tiptap-ui-primitive/button/button.scss';

type PlatformShortcuts = Record<string, string>;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  showTooltip?: boolean;
  tooltip?: React.ReactNode;
  shortcutKeys?: string;
}

export const MAC_SYMBOLS: PlatformShortcuts = {
  ctrl: '⌘',
  alt: '⌥',
  shift: '⇧',
} as const;

export const formatShortcutKey = (key: string, isMac: boolean): string => {
  if (isMac) {
    const lowerKey = key.toLowerCase();
    return MAC_SYMBOLS[lowerKey] || key.toUpperCase();
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
};

export const parseShortcutKeys = (shortcutKeys: string | undefined, isMac: boolean): string[] => {
  if (!shortcutKeys) return [];

  return shortcutKeys
    .split('-')
    .map((key) => key.trim())
    .map((key) => formatShortcutKey(key, isMac));
};

export const ShortcutDisplay: React.FC<{ shortcuts: string[] }> = ({ shortcuts }) => {
  if (shortcuts.length === 0) return null;

  return (
    <div>
      {shortcuts.map((key, index) => (
        <React.Fragment key={index}>
          {index > 0 && <kbd>+</kbd>}
          <kbd>{key}</kbd>
        </React.Fragment>
      ))}
    </div>
  );
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = '', children, tooltip, showTooltip = true, shortcutKeys, 'aria-label': ariaLabel, ...props }, ref) => {
    const isMac = React.useMemo(
      () => typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac'),
      []
    );

    const shortcuts = React.useMemo(() => parseShortcutKeys(shortcutKeys, isMac), [shortcutKeys, isMac]);

    if (!tooltip || !showTooltip) {
      return (
        <button
          type="button"
          className={`tiptap-button ${className}`.trim()}
          ref={ref}
          aria-label={ariaLabel}
          {...props}
        >
          {children}
        </button>
      );
    }

    return (
      <Tooltip delay={200}>
        <TooltipTrigger className={`tiptap-button ${className}`.trim()} ref={ref} aria-label={ariaLabel} {...props}>
          {children}
        </TooltipTrigger>
        <TooltipContent>
          {tooltip}
          <ShortcutDisplay shortcuts={shortcuts} />
        </TooltipContent>
      </Tooltip>
    );
  }
);

Button.displayName = 'Button';

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical';
  children: React.ReactNode;
}

export const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className = '', orientation = 'horizontal', children, ...props }, ref) => (
    <div
      ref={ref}
      className={`tiptap-button-group ${className}`.trim()}
      data-orientation={orientation}
      role="group"
      {...props}
    >
      {children}
    </div>
  )
);

ButtonGroup.displayName = 'ButtonGroup';

export default Button;
